/**
 * Sentinel shared UI kit — friendly building blocks used across the app.
 * Calm palette, rounded cards, tooltips everywhere. No gradients on content.
 */
import { useState, useRef } from 'react';

export const API = process.env.REACT_APP_API_URL;

// Thin fetch wrapper for /api/sentinel. Throws a clean message on failure.
export async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API}/api/sentinel${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.message || 'Something went wrong. Please try again.');
  return json;
}

export const cx = (...a) => a.filter(Boolean).join(' ');

// Hover tooltip — plain-English help on any control. Mirrors the FDR pattern.
export function Tooltip({ text, children, width = 280 }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const enter = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: Math.max(8, r.left + r.width / 2 - width / 2) });
    setShow(true);
  };
  return (
    <span ref={ref} onMouseEnter={enter} onMouseLeave={() => setShow(false)} className="relative inline-flex">
      {children}
      {show && (
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, width, zIndex: 9999 }}
          className="px-3 py-2.5 bg-slate-900 text-white text-xs leading-relaxed rounded-xl shadow-xl pointer-events-none">
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-900 rotate-45 rounded-sm" />
          {text}
        </div>
      )}
    </span>
  );
}

// A tiny "?" that explains a concept on hover — used liberally so nothing feels engineered.
export function Hint({ text }) {
  return (
    <Tooltip text={text}>
      <span className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold cursor-help hover:bg-slate-300">?</span>
    </Tooltip>
  );
}

// Subtle AI mark — Heroicons "sparkles" outline. Matches the app's icon theme
// (calm, modern, no emoji). Use inside AI-action buttons in place of any glyph.
export function AiGlyph({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  );
}

export function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-800',
    green: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-amber-100 text-amber-700',
  };
  return <span className={cx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', tones[tone] || tones.slate)}>{children}</span>;
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
      <span className="w-4 h-4 border-2 border-slate-300 border-t-red-500 rounded-full animate-spin" />
      {label}
    </div>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-16 px-6">
      <p className="text-slate-700 font-semibold">{title}</p>
      {hint && <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">{hint}</p>}
    </div>
  );
}

// ---- Plain-English label maps ----
export const STAGE_LABEL = {
  intake: 'New referral', triage: 'Triage', investigation: 'Investigating',
  pending_records: 'Awaiting records', clinical_review: 'Clinical review',
  pending_recovery: 'Recovery', referred: 'Referred', monitoring: 'Monitoring', closed: 'Closed',
};
export const STAGE_TONE = {
  intake: 'blue', triage: 'amber', investigation: 'purple', pending_records: 'amber',
  clinical_review: 'purple', pending_recovery: 'amber', referred: 'green', monitoring: 'slate', closed: 'slate',
};
export const CHANNEL_LABEL = {
  call_center: 'Call center', email: 'Email', medicaid: 'Medicaid', cms: 'CMS', imedic: 'I-MEDIC',
  hotline_anon: 'Hotline (anonymous)', hotline_named: 'Hotline', web_form: 'Web form',
  internal_claims: 'Claims', internal_um: 'Utilization Management', internal_pharmacy: 'Pharmacy',
  internal_appeals: 'Appeals', data_mining: 'Data mining', external_plan: 'Another plan',
  law_enforcement: 'Law enforcement', internal: 'Internal', other: 'Other',
};
export const CATEGORY_LABEL = {
  services_not_rendered: 'Billing for services not rendered', upcoding: 'Upcoding',
  unbundling: 'Unbundling', duplicate_billing: 'Duplicate billing', medically_unnecessary: 'Medically unnecessary',
  misrepresentation: 'Misrepresentation of service', kickbacks: 'Kickbacks / inducements',
  identity_eligibility: 'Identity / eligibility', pharmacy: 'Pharmacy', dme: 'DME / supplies',
  telehealth: 'Telehealth', lab: 'Lab', behavioral_health: 'Behavioral health',
  risk_adjustment: 'Risk adjustment (HCC)', member_driven: 'Member-driven',
  agent_broker: 'Agent / broker', internal_employee: 'Internal / employee', other: 'Other',
};
export const PRIORITY_TONE = { critical: 'red', high: 'amber', medium: 'blue', low: 'slate' };

export const SUBJECT_TYPE_LABEL = {
  provider_individual: 'Provider (individual)', provider_group: 'Provider group', facility: 'Facility',
  pharmacy: 'Pharmacy', dme_supplier: 'DME supplier', member: 'Member',
  broker_agent: 'Broker / agent', employee: 'Employee', vendor: 'Vendor',
};

export const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
export const dateShort = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
export const dateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
